export default {
    title: 'Package Room',
    listOfDeliveredPackages: 'List of delivered packages',
    photo: 'Photo',
    recipientName: 'Recipient name',
    carrierName: 'Carrier name',
    deliveryAddress: 'Delivery address',
    recipientEmail: 'Recipient email',
    packagesTab: 'Package Room',
    deliveryTab: 'Delivery',
    settingsTab: 'Settings',
    settingsTitle: 'Package Room Settings',
    settingsDescription: 'Controls what couriers can see about residents when they use the intercom directory.',
    scan: {
        title: 'Who can scan packages',
        description:
            'Front desk staff scan parcels in with the mobile app. Pick the user groups whose members are allowed to - only the people in a group matter here, the doors it opens make no difference. Account admins do not need to be in a group. Scanning also has to be switched on for the building.',
        groups: 'User groups allowed to scan',
        saved: 'Scanning access updated',
        createGroup: 'Create group',
        createGroupTitle: 'Create an access group',
        groupName: 'Group name',
        nameRequired: 'Please give the group a name',
        createOnly: 'Create',
        createAndAssign: 'Create and add people',
        addPeopleTitle: 'Add people to the new group',
        peopleAdded: 'People added to the group',
    },
    settingsLoadFailed: 'Could not load the current setting. Reload the page before changing it.',
    directoryDisplayMode: 'Courier Directory Privacy',
    directoryDisplayModeHint:
        'How much of a resident name a courier sees when searching the intercom directory. Unit only also turns off searching by name.',
    maskedName: 'Masked name and unit (J. Smith)',
    fullName: 'Full name and unit (Jane Smith)',
    unitOnly: 'Unit only (name search disabled)',
    packages: 'Packages',
    packagesEmpty: 'No packages match these filters',
    packagesTitle: 'Packages',
    states: {
        delivered: 'Waiting for pickup',
        leftOutside: 'Left outside',
        pickedUp: 'Picked up',
        confirmed: 'Confirmed',
        needsMatch: 'Needs a recipient',
        notMine: 'Wrong recipient',
        returnedToSender: 'Returned to sender',
        closed: 'Closed',
    },
    flags: {
        notMyPackage: 'Not their package',
        pickupDisputed: 'Pickup disputed',
        reportedMissing: 'Reported missing',
        damaged: 'Damaged',
        perishable: 'Perishable',
        movedOut: 'Resident moved out',
        staleEscalated: 'Waiting too long',
        returnToSenderDue: 'Due to return',
        offlineUnattributed: 'Reader was offline',
        unlockFailed: 'Door never opened',
        pinOwnerMismatch: 'Courier chose a different resident',
    },
    actions: {
        title: 'Actions',
        notify: 'Send reminder',
        markPickedUp: 'Mark as picked up',
        markReturned: 'Mark returned to sender',
        close: 'Close package',
        reassign: 'Change recipient',
        pauseReminders: 'Pause reminders',
        resumeReminders: 'Resume reminders',
        clearFlag: 'Clear',
        viewLogs: 'View access events',
        watchVideo: 'Video',
        noteLabel: 'Reason',
        noteHelp: 'Saved to this package history, so anyone reviewing a dispute later can see why this was done.',
        closeDesc: 'Archives the package and stops reminders. For a package left outside, this is the only way to clear it.',
        markReturnedDesc: 'Records that the package went back to the carrier.',
        reassignDesc: 'Moves it to another resident and notifies them.',
        notifyDesc: 'Sends the recipient a reminder right now.',
        noteRequired: 'Please give a reason',
        reassignHelp: 'Pick the resident this package actually belongs to.',
        done: 'Package updated',
        notifySent: 'Reminder sent',
        notifyConfirm: 'Send this resident a reminder now? They get a push, text or email straight away, and it cannot be taken back.',
        viewDetails: 'View details',
        more: 'More',
        bulkNotify: 'Remind everyone still waiting for pickup',
        bulkNotifyConfirm:
            'Remind every resident whose package is still waiting for pickup in this list? Packages already collected, returned or closed are skipped. Up to 500 are sent at a time.',
        bulkNotifyDone: 'Reminders sent. Packages/recipients:',
    },
    sessionTypes: {
        delivery: 'Delivery',
        pickup: 'Pickup',
        staff_intake: 'Staff intake',
        leave_outside: 'Left outside',
    },
    entryMethods: {
        carrier_pin: 'Carrier PIN',
        resident_delivery_pin: 'Resident delivery PIN',
        intercom_call: 'Intercom call',
        front_desk_call: 'Front desk call',
        staff: 'Staff',
        none: 'No entry',
    },
    sessionStatuses: {
        pending: 'Pending',
        unlocked: 'Door opened',
        completed: 'Completed',
        abandoned: 'Abandoned',
        denied: 'Denied',
    },
    intakeMethods: {
        courier_selection: 'Courier chose the resident',
        resident_pin_implicit: 'Resident delivery PIN',
        intercom_call: 'Intercom call',
        staff_ocr: 'Staff scan',
        staff_manual: 'Staff entered manually',
        admin: 'Added by an admin',
        offline_pin_event: 'Reader was offline',
    },
    eventTypes: {
        state_change: 'Status changed',
        notification_sent: 'Notification sent',
        reminder_sent: 'Reminder sent',
        reassigned: 'Recipient changed',
        flag_raised: 'Exception raised',
        flag_cleared: 'Exception cleared',
        admin_action: 'Admin action',
    },
    actorTypes: {
        system: 'System',
        resident: 'Resident',
        admin: 'Admin',
        courier_device: 'Intercom',
        staff: 'Staff',
        cron: 'Scheduled job',
    },
    columns: {
        photo: 'Photo',
        video: 'Video',
        state: 'Status',
        flags: 'Exceptions',
        recipient: 'Recipient',
        unit: 'Unit',
        carrier: 'Carrier',
        tracking: 'Tracking number',
        deliveredAt: 'Delivered',
        daysWaiting: 'Days waiting',
        reminders: 'Reminders',
    },
    filters: {
        state: 'Status',
        carrier: 'Carrier',
        search: 'Recipient or unit',
        flaggedOnly: 'Exceptions only',
        leftOutsideOnly: 'Left outside only',
        dateFrom: 'Delivered from',
        dateTo: 'Delivered to',
    },
    details: {
        title: 'Package',
        titleFor: 'Package for',
        summary: 'Package details',
        photos: 'Photos and video',
        door: 'Door',
        packagesInVisit: 'Dropped in this visit',
        packagesCount: (count: number): string => `${count} packages`,
        notRecorded: 'Not recorded',
        notYet: 'Not yet',
        none: 'None',
        waiting: 'Waiting',
        daysCount: (days: number): string => (days === 1 ? '1 day' : `${days} days`),
        remindersSent: 'Reminders sent',
        noExceptions: 'None',
        technicalDetails: 'Technical details',
        kinds: { state: 'Status', flag: 'Exception', message: 'Message' } as Record<string, string>,
        arrived: 'arrived',
        photoOf: 'Courier at the door',
        labelOf: 'Shipping label',
        clipOf: 'Delivery clip',
        noClipShort: 'No video',
        noClipWhere: 'Nothing was recording this door',
        noClipAtDoor: (door: string): string => `Nothing was recording door ${door}`,
        byWhom: 'by',
        notification: 'Notification',
        sent: 'Sent',
        notSent: 'Not sent',
        openedAt: 'Opened',
        notPickedUpYet: 'Appears here when the resident opens the door to collect it.',
        courierPhoto: 'Courier photo',
        labelPhoto: 'Label photo',
        noPhotos: 'No photos were captured for this package',
        sessions: 'Door activity',
        deliverySession: 'Delivery',
        pickupSession: 'Pickup',
        noSessions: 'No door activity recorded',
        auditTrail: 'History',
        noAuditTrail: 'No history recorded yet',
        notifications: 'Notifications sent',
        noNotifications: 'No notifications sent yet',
        intakeMethod: 'How it arrived',
        notes: 'Notes',
        remindersPaused: 'Paused',
        remindersPausedHelp: 'Automatic reminders are switched off for this package.',
        entryMethod: 'Entry method',
        unlockedAt: 'Opened',
        photosExpire: 'Photo links expire after an hour; reload the page if one fails to open.',
        pickedUpAt: 'Picked up',
        confirmedAt: 'Confirmed',
        reminderCount: 'Reminder count',
        sessionType: 'Session type',
        when: 'When',
        whatHappened: 'What happened',
        who: 'Who',
        video: 'Delivery video',
        noVideo: 'No video was recorded for this delivery',
        videoFailed: 'Could not load the video for this delivery. Reload the page to try again.',
        noVideoHelp: 'Video needs cameras recording at this door at the time of the delivery.',
        viewInFeed: 'View access events',
    },
};
